import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { appRoutes } from "../../constants/appRoutes";
import { storageKeys } from "../../constants/storageKeys";
import { QuarryContextType } from "../../Types/quarryDatabaseTypes";

interface QuarryDatabaseFiltersContextType {
  quarryDatabaseFilters: QuarryContextType;
  updateQuarryDatabaseFilters: (params: Partial<QuarryContextType>) => void;
  resetQuarryDatabaseFilters: () => void;
}

const QuarryDatabaseFiltersContext = createContext<
  QuarryDatabaseFiltersContextType | undefined
>(undefined);

export const useQuarryDatabaseContext =
  (): QuarryDatabaseFiltersContextType => {
    const context = useContext(QuarryDatabaseFiltersContext);
    if (!context) {
      throw new Error(
        "useQuarryDatabaseContext must be used within a QuarryDatabaseFilterContextProvider",
      );
    }
    return context;
  };

const initialFilters: QuarryContextType = {
  materialType: undefined,
  category: undefined,
  address: undefined,
  latitude: undefined,
  longitude: undefined,
  radius: undefined,
  search: undefined,
  sortBy: undefined,
  sortOrder: undefined,
};

export const QuarryDatabaseFilterContextProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const location = useLocation();
  const [quarryDatabaseFilters, setQuarryDatabaseFilters] =
    useState<QuarryContextType>(initialFilters);

  const updateQuarryDatabaseFilters = useCallback(
    (params: QuarryContextType) => {
      setQuarryDatabaseFilters((prevState) => {
        const nextFilters = { ...prevState, ...params };
        sessionStorage.setItem(
          storageKeys.QUARRY_DB_FILTERS,
          JSON.stringify(nextFilters),
        );
        return nextFilters;
      });
    },
    [],
  );

  const resetQuarryDatabaseFilters = () => {
    setQuarryDatabaseFilters(initialFilters);
    sessionStorage.removeItem(storageKeys.QUARRY_DB_FILTERS);
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem(
        storageKeys.QUARRY_DB_FILTERS,
        JSON.stringify(quarryDatabaseFilters),
      );
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [quarryDatabaseFilters]);

  useEffect(() => {
    if (
      location &&
      !location?.pathname?.startsWith(appRoutes.QUARRY_TOOL) &&
      !location?.pathname?.startsWith(appRoutes.QUARRY_PROFILE)
    ) {
      resetQuarryDatabaseFilters();
      sessionStorage.removeItem(storageKeys.QUARRY_DB_PAGE);
    }
  }, [window.location.pathname]);

  return (
    <QuarryDatabaseFiltersContext.Provider
      value={{
        quarryDatabaseFilters,
        updateQuarryDatabaseFilters,
        resetQuarryDatabaseFilters,
      }}
    >
      {children}
    </QuarryDatabaseFiltersContext.Provider>
  );
};
